<template>
    <div id="page-sign-in" data-page="sign-in" class="auth-page-container d-flex align-items-center justify-content-center min-vh-100 py-4 py-md-5 px-3">
        <div class="auth-card p-4 px-md-5 py-md-4 rounded shadow-lg text-center">
            <div class="mb-3 text-center">
                <div class="mb-1">
                    <router-link to="/" class="d-inline-flex align-items-center text-decoration-none">
                        <img :src="logoUrl" alt="VelocyTrack Logo" class="auth-logo mr-2" />
                        <h3 class="font-weight-bold text-white mb-0" style="font-size: 1.6rem; letter-spacing: -0.02em;">Sign In to VelocyTrack</h3>
                    </router-link>
                </div>
                <p class="text-muted small mb-0">Welcome to the VelocyTrack Beta.</p>
            </div>

            <div class="mb-4 p-3 p-md-4 rounded text-left beta-demo-box">
                <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between">
                    <div class="mr-md-3 mb-3 mb-md-0">
                        <div class="small text-info font-weight-bold mb-1 d-flex align-items-center">
                            <i class="bi bi-stars mr-2"></i> VelocyTrack Beta Access
                        </div>
                        <div class="small text-light" style="color: #cbd5e1; font-size: 0.85rem; line-height: 1.4;">
                            Sign in with your registered Beta credentials or SSO, or explore all features immediately in our interactive demo sandbox.
                        </div>
                    </div>
                    <b-button variant="primary" class="btn-aceternity-primary font-weight-bold py-2 px-3 text-nowrap flex-shrink-0" :disabled="loadingDemo" data-test="btn-demo-signin" :data-state="loadingDemo ? 'loading' : 'idle'" @click="launchInteractiveDemo">
                        <b-spinner small v-if="loadingDemo" class="mr-2"></b-spinner>
                        <span v-else><i class="bi bi-play-circle-fill mr-1"></i> Launch Demo</span>
                    </b-button>
                </div>
            </div>

            <b-alert variant="danger" :show="!!errorMessage && !pendingClaim" dismissible @dismissed="errorMessage = ''" class="text-left small mb-4 shadow-sm" data-test="alert-signin-error" style="border-radius: 8px; line-height: 1.5;">
                <div class="d-flex align-items-start">
                    <i class="bi bi-exclamation-triangle-fill mr-3 mt-1 text-danger flex-shrink-0" style="font-size: 1.25rem;"></i>
                    <div class="flex-grow-1">
                        <div v-if="isRestrictedAccessError" class="font-weight-bold mb-1" style="font-size: 0.95rem;">Access Restricted</div>
                        <div>{{ errorMessage }}</div>
                        <div v-if="errorMessage && errorMessage.toLowerCase().includes('waitlist')" class="mt-2 pt-2 border-top" style="border-top-color: rgba(220, 53, 69, 0.3) !important;">
                            <router-link to="/#waitlist" class="text-white font-weight-bold text-decoration-underline">
                                <i class="bi bi-arrow-right-circle mr-1"></i> Request Beta Access via Waitlist
                            </router-link>
                        </div>
                    </div>
                </div>
            </b-alert>

            <!-- Active Session Detection Banner -->
            <div v-if="activeSession && !pendingClaim" class="active-session-banner p-3 mb-4 rounded border text-left" data-test="banner-active-session">
                <div class="d-flex align-items-center justify-content-between flex-wrap">
                    <div class="d-flex align-items-center mb-2 mb-sm-0">
                        <b-avatar :src="activeSession.picture" :text="getInitials(activeSession.fullName)" size="42px" variant="info" class="mr-3 border border-secondary flex-shrink-0" data-test="avatar-active-session"></b-avatar>
                        <div>
                            <div class="d-flex align-items-center flex-wrap">
                                <span class="text-white font-weight-bold mr-2">{{ activeSession.fullName || activeSession.email }}</span>
                                <b-badge variant="info" class="badge-pill font-weight-normal py-1 px-2" style="font-size: 0.72rem;">
                                    <i class="bi bi-shield-check mr-1"></i>Active Session Cookie
                                </b-badge>
                            </div>
                            <div class="text-muted small">{{ activeSession.email }}</div>
                        </div>
                    </div>
                    <div class="d-flex align-items-center">
                        <b-button size="sm" variant="primary" to="/timelog" class="mr-2 font-weight-bold" data-test="btn-continue-active-session">
                            Continue <i class="bi bi-arrow-right ml-1"></i>
                        </b-button>
                        <b-button size="sm" variant="outline-danger" @click="signOutActiveSession" data-test="btn-signout-active-session" title="Sign out of this session cookie">
                            <i class="bi bi-box-arrow-right mr-1"></i>Sign Out
                        </b-button>
                    </div>
                </div>
            </div>

            <!-- Inline Instant Beta Claim Panel for SSO Users -->
            <div v-if="pendingClaim" class="sso-claim-panel p-4 mb-4 rounded border text-left shadow-lg" data-test="panel-sso-claim">
                <div class="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom border-secondary">
                    <div class="d-flex align-items-center">
                        <b-badge variant="info" class="badge-pill font-weight-bold py-1 px-2 mr-2" style="font-size: 0.75rem;">
                            <i class="bi bi-stars mr-1"></i> Verified Identity
                        </b-badge>
                        <span class="text-muted small">Step 2 of 2: Claim Beta Spot</span>
                    </div>
                    <b-button variant="link" size="sm" class="text-muted p-0 text-decoration-none" data-test="btn-claim-cancel" @click="cancelPendingClaim" title="Cancel and return to sign in">
                        <i class="bi bi-x-lg"></i>
                    </b-button>
                </div>

                <div class="d-flex align-items-center mb-3">
                    <b-avatar :src="pendingClaim.picture" :text="getInitials(pendingClaim.fullName || pendingClaim.email)" size="48px" variant="primary" class="mr-3 border border-secondary flex-shrink-0" data-test="avatar-pending-claim"></b-avatar>
                    <div>
                        <div class="text-white font-weight-bold" style="font-size: 1.05rem;" data-test="text-claim-name">
                            {{ pendingClaim.fullName || 'Welcome!' }}
                        </div>
                        <div class="text-light small d-flex align-items-center" style="color: #cbd5e1;" data-test="text-claim-email">
                            <i v-if="pendingClaim.provider === 'Google'" class="bi bi-google text-primary mr-1"></i>
                            <i v-else-if="pendingClaim.provider === 'GitHub'" class="bi bi-github text-light mr-1"></i>
                            <i v-else class="bi bi-shield-lock text-info mr-1"></i>
                            {{ pendingClaim.email }}
                        </div>
                    </div>
                </div>

                <p class="text-muted small mb-3" style="line-height: 1.5;">
                    Your <strong class="text-white">{{ pendingClaim.provider || 'SSO' }}</strong> identity is verified. Claim your beta spot below to activate your account and start immediately.
                </p>

                <!-- Claim Result Alerts (queued, closed, error) -->
                <b-alert v-if="claimResult && claimResult.status === 'queued'" variant="info" show class="small mb-3" data-test="alert-claim-queued">
                    <i class="bi bi-hourglass-split mr-2"></i>
                    {{ claimResult.message }}
                </b-alert>
                <b-alert v-else-if="claimResult && claimResult.status === 'alreadyClaimed'" variant="info" show class="small mb-3" data-test="alert-claim-queued">
                    <i class="bi bi-info-circle mr-2"></i>
                    {{ claimResult.message }}
                </b-alert>
                <b-alert v-else-if="claimResult && claimResult.status === 'betaClosed'" variant="warning" show class="small mb-3" data-test="alert-claim-closed">
                    <i class="bi bi-exclamation-triangle mr-2"></i>
                    {{ claimResult.message }}
                </b-alert>
                <b-alert v-else-if="claimResult && !claimResult.success" variant="danger" show class="small mb-3" data-test="alert-claim-error">
                    <i class="bi bi-x-circle mr-2"></i>
                    {{ claimResult.message }}
                </b-alert>

                <div v-if="!claimResult || claimResult.status === 'error' || claimResult.status === 'expired'">
                    <b-button variant="primary" block class="btn-aceternity-primary font-weight-bold py-2 mb-2" :disabled="loadingClaim" data-test="btn-claim-beta-sso" :data-state="loadingClaim ? 'loading' : 'idle'" @click="submitClaimBetaSso">
                        <b-spinner small v-if="loadingClaim" class="mr-2"></b-spinner>
                        <span v-else><i class="bi bi-patch-check-fill mr-2"></i>Claim My Spot &amp; Sign In</span>
                    </b-button>

                    <div class="text-center mt-2">
                        <b-button variant="link" size="sm" class="text-muted text-decoration-none" data-test="btn-claim-use-different-account" @click="cancelPendingClaim">
                            <i class="bi bi-arrow-left mr-1"></i> Use a different account
                        </b-button>
                    </div>
                </div>

                <div v-else class="text-center mt-3 pt-2 border-top border-secondary">
                    <b-button variant="outline-light" size="sm" class="px-3" data-test="btn-claim-return" @click="cancelPendingClaim">
                        Return to Sign In
                    </b-button>
                </div>
            </div>

            <b-row v-if="!pendingClaim" class="text-left align-items-stretch">
                <!-- Direct credentials column -->
                <b-col cols="12" md="6" class="pr-md-4 mb-4 mb-md-0 auth-form-col">
                    <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
                        <b-form @submit.prevent="handleSubmit(onSignIn)" class="h-100 d-flex flex-column justify-content-between">
                            <div>
                                <ValidationProvider rules="required|email" name="Email" v-slot="{ errors }">
                                    <b-form-group label="Email Address" label-class="text-light small font-weight-bold" class="mb-3">
                                        <b-form-input v-model="form.email"
                                                      id="signin-email"
                                                      name="username"
                                                      autocomplete="username"
                                                      type="email"
                                                      placeholder="Enter your email"
                                                      :state="errors.length > 0 ? false : null"
                                                      data-test="input-signin-email"
                                                      class="auth-input">
                                        </b-form-input>
                                        <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                                    </b-form-group>
                                </ValidationProvider>

                                <ValidationProvider rules="required" name="Password" v-slot="{ errors }">
                                    <b-form-group label="Password" label-class="text-light small font-weight-bold" class="mb-4">
                                        <b-form-input v-model="form.password"
                                                      id="signin-password"
                                                      name="password"
                                                      autocomplete="current-password"
                                                      type="password"
                                                      placeholder="Enter your password"
                                                      :state="errors.length > 0 ? false : null"
                                                      data-test="input-signin-password"
                                                      class="auth-input">
                                        </b-form-input>
                                        <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                                    </b-form-group>
                                </ValidationProvider>
                            </div>

                            <b-button type="submit" variant="primary" block class="btn-aceternity-primary font-weight-bold py-2" :disabled="loading" data-test="btn-submit-signin" :data-state="loading ? 'loading' : 'idle'">
                                <b-spinner small v-if="loading" class="mr-2"></b-spinner>
                                Sign In
                            </b-button>
                        </b-form>
                    </ValidationObserver>
                </b-col>

                <!-- Single Sign-On column -->
                <b-col cols="12" md="6" class="pl-md-4 auth-sso-col d-flex flex-column justify-content-between">
                    <div>
                        <p class="text-muted small mb-3 text-center text-md-left">Or sign in with SSO if invited:</p>
                        <div class="sso-buttons-stack d-flex flex-column align-items-center align-items-md-start w-100">
                            <!-- Google SSO Item -->
                            <div class="w-100 mb-3">
                                <google-sign-in :clientId="googleClientId" :width="320" theme="filled_black" text="continue_with" :rememberedAccount="lastGoogleAccount" :suppressToast="true" @success="onGoogleSuccess" @error="onGoogleError" @beta-claim-pending="onBetaClaimPending" class="w-100" data-test="btn-signin-google"></google-sign-in>
                            </div>

                            <!-- GitHub SSO Item -->
                            <div class="w-100 mb-3">
                                <b-button variant="outline-light" class="w-100 d-flex align-items-center justify-content-between sso-btn px-3" data-test="btn-signin-github" aria-label="Sign in with GitHub" title="Sign in with GitHub" @click="loginOAuth('GitHub')">
                                    <div class="d-flex align-items-center">
                                        <i class="bi bi-github mr-2 font-size-18"></i>
                                        <span>Continue with GitHub</span>
                                    </div>
                                    <div v-if="lastGithubAccount" class="d-flex align-items-center ml-2" data-test="avatar-github-account" :title="'Last used: ' + (lastGithubAccount.name || lastGithubAccount.email)">
                                        <b-avatar :src="lastGithubAccount.picture" :text="getInitials(lastGithubAccount.name)" size="24px" class="border border-secondary"></b-avatar>
                                    </div>
                                </b-button>
                            </div>
                        </div>
                    </div>
                </b-col>
            </b-row>

            <div class="text-center mt-4 pt-3 border-top border-secondary">
                <p class="text-muted small mb-0" style="font-size: 0.82rem;">
                    By signing in, you agree to our 
                    <a href="#" @click.prevent="openLegal('terms')" class="text-info text-decoration-none font-weight-bold">Terms of Service</a>
                    and acknowledge our 
                    <a href="#" @click.prevent="openLegal('privacy')" class="text-info text-decoration-none font-weight-bold">Privacy Policy</a>.
                </p>
            </div>
        </div>
    </div>
</template>

<script>
    import GoogleSignIn from './google-sign-in.vue';
    import { ValidationObserver, ValidationProvider } from 'vee-validate';
    import accountMixin from '../mixinAccount';
    import eventBus from '../event-bus';

    export default {
        name: 'page-sign-in',
        mixins: [accountMixin],
        components: {
            GoogleSignIn,
            ValidationObserver,
            ValidationProvider
        },
        data() {
            return {
                form: {
                    email: '',
                    password: ''
                },
                errorMessage: '',
                loading: false,
                loadingDemo: false,
                activeSession: null,
                lastGoogleAccount: null,
                lastGithubAccount: null,
                lastEmailAccount: null,
                pendingClaim: null,
                loadingClaim: false,
                claimResult: null
            };
        },
        computed: {
            googleClientId() {
                return process.env.VUE_APP_GOOGLE_CLIENT_ID;
            },
            isRestrictedAccessError() {
                if (!this.errorMessage) return false;
                const msg = this.errorMessage.toLowerCase();
                return msg.includes('restricted') || msg.includes('waitlist') || msg.includes('invitation');
            }
        },
        watch: {
            '$route.query': {
                immediate: true,
                handler(newQuery) {
                    if (newQuery && newQuery.error) {
                        this.errorMessage = newQuery.error;
                    }
                    if (newQuery && newQuery.claim === 'pending') {
                        this.checkPendingSsoClaim();
                    }
                }
            }
        },









        async mounted() {
            // Force dark theme for the auth container globally
            document.documentElement.setAttribute('data-theme', 'dark');
            this.loadRememberedAccounts();

            const token = (this.$route.query.token || this.$route.query.invite || '').toString().trim();
            if (token) {
                try {
                    localStorage.setItem('velocytrack_invite_token', token);
                } catch (e) {
                    // Ignore
                }
                this.$router.replace(`/sign-up/${encodeURIComponent(token)}`).catch(() => {});
                return;
            }

            if (this.$route.query.claim !== 'pending') { await this.checkActiveSession(); }
        },
        methods: {
            getInitials(name) {
                if (!name) return 'U';
                const parts = name.trim().split(/\s+/);
                if (parts.length >= 2) {
                    return (parts[0][0] + parts[1][0]).toUpperCase();
                }
                return name.slice(0, 2).toUpperCase();
            },
            loadRememberedAccounts() {
                try {
                    const g = localStorage.getItem('velocytrack_last_google_account');
                    if (g) this.lastGoogleAccount = JSON.parse(g);
                } catch (_) {
                    // Ignore localStorage errors
                }
                try {
                    const gh = localStorage.getItem('velocytrack_last_github_account');
                    if (gh) this.lastGithubAccount = JSON.parse(gh);
                } catch (_) {
                    // Ignore localStorage errors
                }
                try {
                    const em = localStorage.getItem('velocytrack_last_email_account');
                    if (em) {
                        this.lastEmailAccount = JSON.parse(em);
                        if (!this.form.email && this.lastEmailAccount.email) {
                            this.form.email = this.lastEmailAccount.email;
                        }
                    }
                } catch (_) {
                    // Ignore localStorage errors
                }
            },
            async checkActiveSession() {
                if (this.account && this.account.email) {
                    this.activeSession = this.account;
                    return;
                }
                try {
                    const resp = await this.$http.get('/Account/Me');
                    if (resp && resp.data && resp.data.email) {
                        this.activeSession = resp.data;
                        this.$store.commit('setAccount', resp.data);
                        this.$store.commit('setAuthenticated', true);
                    }
                } catch (_) {
                    this.activeSession = null;
                }
            },
            async signOutActiveSession() {
                try {
                    await this.$http.get('/Account/Logout');
                } catch (_) {
                    // Ignore logout errors
                }
                this.$store.commit('setAccount', null);
                this.$store.commit('setAuthenticated', false);
                this.activeSession = null;
            },
            async onSignIn() {
                this.errorMessage = '';
                this.loading = true;
                try {
                    const response = await this.$http.post('/Account/SignIn', this.form);
                    this.$store.commit('setAccount', response.data);
                    this.$store.commit('setAuthenticated', true);
                    try {
                        localStorage.setItem('velocytrack_last_email_account', JSON.stringify({
                            email: response.data.email,
                            name: response.data.fullName,
                            picture: response.data.picture,
                            provider: 'Password',
                            lastLogin: new Date().toISOString()
                        }));
                    } catch (_) {
                        // Ignore localStorage errors
                    }
                    await this.initSessionConfigAsync();
                    this.$router.push('/timelog').catch(() => {});
                } catch (error) {
                    const respData = error.response?.data;
                    this.errorMessage = respData?.message || 
                                        (typeof respData === 'string' ? respData : null) || 
                                        error.message || 
                                        'Sign in failed. Please check your credentials.';
                } finally {
                    this.loading = false;
                }
            },
            async launchInteractiveDemo() {
                this.loadingDemo = true;
                this.errorMessage = '';
                try {
                    const resp = await this.$http.post('/Demo/StartSession', {
                        firstName: 'Alex',
                        lastName: 'Morgan',
                        email: 'alex.morgan@demo.velocytrack.com',
                        role: 'Developer'
                    });
                    if (resp.data && resp.data.account) {
                        this.$store.commit('setTheme', 'dark');
                        document.documentElement.setAttribute('data-theme', 'dark');
                        this.$store.commit('setAccount', resp.data.account);
                        this.$store.commit('setAuthenticated', true);
                        if (resp.data.config) {
                            this.$store.commit('setConfig', resp.data.config);
                        }
                        await this.initSessionConfigAsync();
                        const targetUrl = resp.data.redirectUrl || '/timelog';
                        this.$router.push(targetUrl).catch(() => {});
                    }
                } catch (err) {
                    this.errorMessage = err.response?.data?.message || 'Error launching interactive demo. Please try again.';
                } finally {
                    this.loadingDemo = false;
                }
            },
            onGoogleSuccess() {
                // google-sign-in component handles redirection itself when emitTokenOnly is false
            },
            onGoogleError(msg) {
                this.errorMessage = typeof msg === 'string' ? msg : (msg?.message || 'Google sign in failed. Please try again.');
            },
            onBetaClaimPending(payload) {
                this.errorMessage = '';
                this.claimResult = null;
                this.pendingClaim = {
                    email: payload.email,
                    fullName: payload.name || payload.fullName,
                    picture: payload.picture,
                    provider: payload.provider || 'Google'
                };
            },
            async checkPendingSsoClaim() {
                try {
                    const resp = await this.$http.get('/Account/PendingSsoClaim');
                    if (resp && resp.data && resp.data.email) {
                        this.pendingClaim = resp.data;
                        this.errorMessage = '';
                        return;
                    }
                } catch (_) {
                    // Fall back to query params if present
                }
                if (this.$route.query.email) {
                    this.pendingClaim = {
                        email: this.$route.query.email,
                        provider: this.$route.query.provider || 'SSO'
                    };
                    this.errorMessage = '';
                }
            },
            async submitClaimBetaSso() {
                if (!this.pendingClaim) return;
                this.loadingClaim = true;
                this.claimResult = null;
                this.errorMessage = '';
                try {
                    const resp = await this.$http.post('/Account/ClaimBetaFromSso', {
                        email: this.pendingClaim.email
                    });
                    const data = resp.data;
                    if (data.status === 'signedIn' && data.account) {
                        this.$store.commit('setAccount', data.account);
                        this.$store.commit('setAuthenticated', true);
                        await this.initSessionConfigAsync();
                        this.$router.push('/timelog').catch(() => {});
                        return;
                    }
                    this.claimResult = data;
                } catch (err) {
                    const respData = err.response?.data;
                    this.claimResult = {
                        success: false,
                        status: respData?.status || 'error',
                        message: respData?.message || (typeof respData === 'string' ? respData : null) || 'Unable to claim your beta spot. Please try again.'
                    };
                } finally {
                    this.loadingClaim = false;
                }
            },
            async cancelPendingClaim() {
                this.pendingClaim = null;
                this.claimResult = null;
                try {
                    await this.$http.post('/Account/ClearPendingSsoClaim');
                } catch (_) {
                    // Ignore
                }
                if (this.$route.query.claim || this.$route.query.error) {
                    this.$router.replace({ path: '/sign-in', query: {} }).catch(() => {});
                }
            },
            loginOAuth(provider) {
                const returnUrl = encodeURIComponent(window.location.origin);
                window.location.href = `${this.$http.defaults.baseURL}/Account/ExternalLogin?provider=${provider}&returnUrl=${returnUrl}&prompt=select_account`;
            },
            openLegal(tab) {
                eventBus.$emit('open-legal', tab);
            }
        }
    };
</script>

<style scoped>
    .auth-page-container {
        background-color: #030712; /* Deep dark background matching landing */
        color: #f1f5f9;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    }

    .auth-card {
        background-color: #0f172a;
        border: 1px solid rgba(255, 255, 255, 0.08);
        width: 100%;
        max-width: 760px;
    }

    .beta-demo-box {
        background: rgba(56, 189, 248, 0.08);
        border: 1px solid rgba(56, 189, 248, 0.25);
    }

    .auth-logo {
        height: 34px;
        width: 34px;
        object-fit: contain;
        border-radius: 6px;
        box-shadow: 0 0 15px rgba(56, 189, 248, 0.3);
    }

    .auth-input {
        background-color: #1e293b;
        border-color: rgba(255, 255, 255, 0.15);
        color: #fff;
    }

    .auth-input:focus {
        background-color: #1e293b;
        border-color: #38bdf8;
        color: #fff;
        box-shadow: 0 0 0 0.2rem rgba(56, 189, 248, 0.25);
    }

    .btn-aceternity-primary {
        background: linear-gradient(135deg, #0284c7, #38bdf8);
        border: none;
        color: white;
        transition: transform 0.2s, box-shadow 0.2s;
    }

    .btn-aceternity-primary:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 10px 20px -10px rgba(56, 189, 248, 0.5);
    }

    .sso-buttons-stack {
        width: 100%;
        max-width: 320px;
    }

    .sso-btn {
        background-color: #131314;
        border: 1px solid #3c4043;
        color: #e3e3e3;
        font-size: 14px;
        font-weight: 500;
        height: 40px;
        border-radius: 4px;
        box-sizing: border-box;
        transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
    }

    .sso-btn:hover {
        background-color: #202124;
        border-color: #5f6368;
        color: #ffffff;
    }

    .sso-btn:active {
        background-color: #303134;
    }

    @media (min-width: 768px) {
        .auth-form-col {
            border-right: 1px solid rgba(255, 255, 255, 0.1);
        }
    }

    @media (max-width: 767.98px) {
        .auth-sso-col {
            border-top: 1px solid rgba(255, 255, 255, 0.1);
            padding-top: 0.9rem;
        }

        .auth-page-container {
            padding-top: 0.75rem !important;
            padding-bottom: 0.75rem !important;
            align-items: flex-start !important;
        }

        .auth-card {
            padding: 1rem 0.95rem !important;
        }

        .auth-card h3 {
            font-size: 1.25rem !important;
        }

        .beta-demo-box {
            padding: 0.65rem 0.75rem !important;
            margin-bottom: 0.85rem !important;
        }

        .auth-form-col {
            margin-bottom: 0.85rem !important;
        }
    }

    @media (max-width: 575.98px) {
        .beta-demo-box .text-light {
            display: none;
        }
        .beta-demo-box > div {
            flex-direction: row !important;
            align-items: center !important;
        }
        .beta-demo-box > div > div {
            margin-bottom: 0 !important;
            margin-right: 0.5rem !important;
        }
        .beta-demo-box .btn-aceternity-primary {
            padding: 0.35rem 0.75rem !important;
            font-size: 0.82rem !important;
        }
        .signin-mobile-noop {
        }
    }

    .active-session-banner {
        background: rgba(14, 165, 233, 0.08);
        border-color: rgba(14, 165, 233, 0.3) !important;
    }

    .sso-claim-panel {
        background: rgba(15, 23, 42, 0.95);
        border: 1px solid rgba(56, 189, 248, 0.35) !important;
        box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 0 20px -5px rgba(56, 189, 248, 0.15);
    }
</style>
